import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import type {BusinessCaseResponseBody, CheckoutResponseBody} from '@dms/api/sales';

import type {
  BusinessCasePaymentEvidence,
  BusinessCasePaymentKind,
  BusinessCasePaymentState,
} from '@prompt-bridge/shared/ai-assistant-core';

type CheckoutPayment = Pick<
  CheckoutResponseBody['orders'][number]['payments'][number],
  'id' | 'paymentDiscriminator' | 'amount' | 'payDate' | 'issuedOn' | 'dueDate' | 'paymentState'
>;

type CheckoutPaymentSource = {
  orders: readonly {payments: readonly CheckoutPayment[]}[];
};

const toPaymentState = (
  payment: CheckoutPayment,
  businessCaseState: BusinessCaseResponseBody['businessCaseState']
): BusinessCasePaymentState =>
  match({
    businessCaseState,
    discriminator: payment.paymentDiscriminator,
    hasPaidAt: isNotNil(payment.payDate),
    paymentState: payment.paymentState,
  })
    .returnType<BusinessCasePaymentState>()
    .with({paymentState: 'PAID'}, {hasPaidAt: true}, () => 'paid')
    .with(
      {discriminator: 'DEPOSIT', businessCaseState: 'DEPOSIT_PAID'},
      {discriminator: 'DEPOSIT', businessCaseState: 'INVOICE'},
      {discriminator: 'DEPOSIT', businessCaseState: 'PAID'},
      {discriminator: 'BALANCE', businessCaseState: 'PAID'},
      () => 'paid'
    )
    .when(
      ({paymentState}) => paymentState === 'PENDING' || paymentState === 'CONCEPT',
      () => (isNotNil(payment.issuedOn) ? 'unpaid' : 'unknown')
    )
    .otherwise(() => 'unknown');

const toPaymentAmount = (
  payment: CheckoutPayment
): Pick<BusinessCasePaymentEvidence, 'amount' | 'currency'> => {
  if (isNil(payment.amount)) {
    return {};
  }

  const amount = Number(payment.amount.amount);
  return Number.isFinite(amount) ? {amount, currency: payment.amount.currency} : {};
};

const toPaymentEvidence = (
  payment: CheckoutPayment,
  kind: BusinessCasePaymentKind,
  businessCaseState: BusinessCaseResponseBody['businessCaseState']
): BusinessCasePaymentEvidence => ({
  id: payment.id,
  kind,
  ...toPaymentAmount(payment),
  issuedAt: payment.issuedOn,
  dueOn: payment.dueDate,
  paidAt: payment.payDate,
  state: toPaymentState(payment, businessCaseState),
});

export const toBusinessCasePaymentState = (
  businessCaseState: BusinessCaseResponseBody['businessCaseState'],
  checkout: CheckoutPaymentSource | null
): BusinessCasePaymentEvidence[] =>
  (checkout?.orders ?? []).flatMap((order) =>
    order.payments.flatMap((payment) =>
      match(payment.paymentDiscriminator)
        .returnType<BusinessCasePaymentEvidence[]>()
        .with('DEPOSIT', () => [toPaymentEvidence(payment, 'deposit', businessCaseState)])
        .with('BALANCE', () => [toPaymentEvidence(payment, 'invoice', businessCaseState)])
        .with('PURCHASE', () => [])
        .exhaustive()
    )
  );
